/**
 * Private Voice Chat Domain - Player Side
 * Manages private voice chat state when a moderator initiates a private conversation
 *
 * Accepting leaves the team's room for the moderator's: the team room's token
 * is spent, and the session's own voice switches — a step with voice off, the
 * session's silence — stay as they are, for the team room to apply again when
 * the call ends. The call itself outranks them (domains/your-voice.ts): the
 * moderator is heard, and the microphone is the player's. What the call's end
 * gives back — the player's mute, and whether they were in the team's room —
 * is kept here as they accept, and every way a call ends goes through
 * endPrivateCall, which gives it back before any team room opens. When the
 * moderator ends the call or its room fails, the player tells the server
 * (OpCodePrivateChatLeft), which clears the "with the moderator" mark every
 * teammate sees and answers with a fresh token for the team room — the one
 * from joining has expired by then.
 *
 * A call whose moderator is gone — their tab closed, their portal session
 * expired — ends the same way, on the player's side: the player cannot leave
 * a call, and it would hold them in the call's room, microphone open, with
 * no way back to their team. Only the call's room knows: the moderator
 * is in no match the server runs — the portal reaches it through
 * portal-backend's calls — and LiveKit tells a room's participants who comes
 * and goes, and the server only through webhooks the stack does not take. The
 * server knows a call's start and its end as the moderator's portal says them
 * (the moderation calls), and the player's acceptance and leaving (OpCode 7,
 * OpCode 12). So the player's client watches the room
 * (voice-chat-provider.tsx): a moderator not in it for
 * MODERATOR_GONE_GRACE_MS — gone, or never come — ends the call, and the
 * server, told as for any end, clears the mark and sends the team's token.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { showInfoToast, showWarningToast } from './toasts';
import { restoreVoiceAfterCall, suspendTeamVoiceForCall, useVoiceChatStore } from './voice-chat';
import { oneShotClock } from '../utils/one-shot-clock';
import i18n from '../i18n/config';
import { OpCode, MessageType } from '../constants/op-codes';
import { currentSeconds } from '../utils/use-now-seconds';
import { sendToMatch } from '../services/nakama/match-send';

// TYPES

type PrivateVoiceChatConnectionState = 'disconnected' | 'connecting' | 'connected';

/** How long an invitation waits for the player before it accepts itself, in seconds. */
const INVITATION_ACCEPTS_AFTER = 10;

/**
 * How long a connected call waits for its moderator, gone from its room or
 * never come, before it ends on the player's side, in milliseconds: a
 * moderator whose page reloads is back within it.
 */
const MODERATOR_GONE_GRACE_MS = 30_000;

/**
 * When an invitation that arrived at `invitedAt` accepts itself, in whole Unix
 * seconds: as the second INVITATION_ACCEPTS_AFTER seconds on begins. Its
 * notice counts down to it, and its clock (handlePrivateChatStart) runs out
 * at it.
 */
export function invitationAcceptsAt(invitedAt: number): number {
  return invitedAt + INVITATION_ACCEPTS_AFTER;
}

interface PrivateVoiceChatState {
  // Connection info from server (for private room)
  serverUrl: string | null;
  participantToken: string | null;
  moderatorName: string | null;

  // State
  connectionState: PrivateVoiceChatConnectionState;
  inPrivateChat: boolean;
  isPending: boolean; // New: true when waiting for player to accept
  /**
   * When the pending invitation arrived, in whole Unix seconds — the game's
   * clock (useNowSeconds): it accepts itself as the second
   * INVITATION_ACCEPTS_AFTER seconds on begins.
   */
  invitedAt: number | null;

  /**
   * What the call's end gives back, kept as the player accepted: null while
   * no call they accepted is on. It is what "accepted" means here — an
   * invitation replacing the call before it ends keeps it, since the mute
   * from before the first call is the player's, and the call's is not.
   */
  returnTo: CallReturn | null;

  // Actions
  startPrivateChat: (serverUrl: string, token: string, moderatorName: string) => void;
  /** Into the call's room, keeping what its end gives back unless a call accepted earlier kept it already. */
  acceptPrivateChat: (returnTo: CallReturn) => void;
  setConnectionState: (state: PrivateVoiceChatConnectionState) => void;
  reset: () => void;
}

/** What the end of a moderator's call gives back to the player, as they had it when they accepted. */
interface CallReturn {
  /** They were in the team's room: the end goes back to it. */
  teamRoom: boolean;
  /** Their microphone's mute there. */
  muted: boolean;
}

// STORE

const initialState: Pick<
  PrivateVoiceChatState,
  | 'serverUrl'
  | 'participantToken'
  | 'moderatorName'
  | 'connectionState'
  | 'inPrivateChat'
  | 'isPending'
  | 'returnTo'
  | 'invitedAt'
> = {
  serverUrl: null,
  participantToken: null,
  moderatorName: null,
  connectionState: 'disconnected',
  inPrivateChat: false,
  isPending: false,
  returnTo: null,
  invitedAt: null,
};

export const usePrivateVoiceChatStore = create<PrivateVoiceChatState>()(
  devtools(
    (set): PrivateVoiceChatState => ({
      ...initialState,

      startPrivateChat: (serverUrl, token, moderatorName) => {
        set(
          {
            serverUrl,
            participantToken: token,
            moderatorName,
            connectionState: 'disconnected', // Stay disconnected until accepted
            inPrivateChat: false, // Not yet in chat
            isPending: true, // Waiting for player to accept
            invitedAt: currentSeconds(),
          },
          false,
          'startPrivateChat'
        );
      },

      acceptPrivateChat: (returnTo) => {
        set(
          (state) => ({
            connectionState: 'connecting',
            inPrivateChat: true,
            isPending: false,
            invitedAt: null,
            returnTo: state.returnTo ?? returnTo,
          }),
          false,
          'acceptPrivateChat'
        );
      },

      setConnectionState: (state) => {
        set({ connectionState: state }, false, 'setConnectionState');
      },

      reset: () => {
        set({ ...initialState }, false, 'reset');
      },
    }),
    { name: 'PrivateVoiceChatStore' }
  )
);

/**
 * When the open invitation arrived, or null while none is open: one is open
 * while it waits on the player and names its moderator. Its notice opens on
 * this, and the full-screen gate waits while it is open.
 */
export function openInvitationSince(state: PrivateVoiceChatState): number | null {
  return state.isPending && state.moderatorName ? state.invitedAt : null;
}

/**
 * How the identity the server gives the moderator in a call's room begins
 * (shared/livekit.go: PrivateChatModeratorIdentityPrefix, which
 * client_parity_test.go holds this to): a drift would end every call thirty
 * seconds after it connected, as one whose moderator had gone.
 */
export const CALL_MODERATOR_PREFIX = 'mod_';

/**
 * Whether a participant of the call's room is its moderator, by the identity
 * the server gives them: the call window finds their face by it, and the
 * provider whether they are still there.
 */
export function isCallModerator(identity: string): boolean {
  return identity.startsWith(CALL_MODERATOR_PREFIX);
}

// MESSAGE HANDLERS

/**
 * The open invitation's own clock: it accepts the invitation as its time runs
 * out, whatever is drawn — its notice draws the time (FootClock) and keeps
 * none. Stopped as the invitation is accepted or closed, or the call ends.
 */
const invitationClock = oneShotClock();

/** The clock of a call whose moderator is not in its room: it ends the call as it runs out. Stopped as they come back, or the call ends. */
const moderatorGoneClock = oneShotClock();

/** A moderator's invitation to a private call (OpCodePrivateChatStart), as the router's guard holds it. */
export interface PrivateChatStartMessage {
  type: typeof MessageType.MessageTypePrivateChatStart;
  room_name: string;
  moderator_name: string;
  server_url: string;
  participant_token: string;
}

/** The private call's end (OpCodePrivateChatEnd), as the router's guard holds it. */
export interface PrivateChatEndMessage {
  type: typeof MessageType.MessageTypePrivateChatEnd;
  reason: string;
}

/**
 * Handles private chat start message from server
 * This is called when a moderator initiates a private voice chat with the player
 */
export function handlePrivateChatStart(message: PrivateChatStartMessage): void {
  // The room and who invited, never the token: it admits whoever holds it to the call.
  console.log('[PrivateVoiceChat] Invited to a private chat by', message.moderator_name, 'in', message.room_name);

  const { startPrivateChat } = usePrivateVoiceChatStore.getState();

  // Store connection info to initiate private chat
  startPrivateChat(message.server_url, message.participant_token, message.moderator_name);

  // It accepts itself in INVITATION_ACCEPTS_AFTER seconds: an invitation that
  // replaces one still waiting starts the time again.
  const invitedAt = usePrivateVoiceChatStore.getState().invitedAt ?? currentSeconds();
  invitationClock.start(Math.max(0, invitationAcceptsAt(invitedAt) * 1000 - Date.now()), acceptPrivateChatInvitation);
}

/**
 * Ends the moderator's private call, whichever way it ends — the moderator
 * ends it, its room fails, the socket drops, a moderator removes the player,
 * the player leaves — or closes an invitation they never accepted, which
 * left nothing to give back. A call they accepted gives them back their mute
 * first, as they had it before the call, so whichever team room opens next —
 * the fresh token the server sends after OpCodePrivateChatLeft, a rejoin's,
 * one handed out during the call — opens with the microphone as they left
 * it. With `tellServer` the server is told (OpCodePrivateChatLeft): it
 * clears the mark every teammate sees and sends a fresh team token. A drop, a
 * removal or a leave tells it nothing: the socket is gone or going, and a
 * rejoin starts the player afresh on the server, token and all. True when the
 * player goes back to a team room.
 */
export function endPrivateCall({ tellServer }: { tellServer: boolean }): boolean {
  invitationClock.stop();
  moderatorGoneClock.stop();
  const { returnTo, reset } = usePrivateVoiceChatStore.getState();
  if (returnTo === null) {
    reset();
    return false;
  }

  const voiceChat = useVoiceChatStore.getState();
  restoreVoiceAfterCall(returnTo.muted);
  reset();
  if (tellServer) sendPrivateChatLeft();
  return returnTo.teamRoom || voiceChat.participantToken !== null;
}

/**
 * Handles private chat end message from server
 * This is called when the moderator ends the private chat
 */
export function handlePrivateChatEnd(message: PrivateChatEndMessage): void {
  console.log('[PrivateVoiceChat] Received private chat end:', message);

  // Said as the player goes back to the team's room. An invitation withdrawn
  // before it was accepted just closes: they never left it.
  if (endPrivateCall({ tellServer: true })) {
    showInfoToast(i18n.t('privateChat.ended'), i18n.t('privateChat.backToTeam'), 3000);
  }
}

/**
 * The private room has gone for good (LiveKit's Disconnected, from
 * voice-chat-provider.tsx). Either it never connected — refused, unreachable,
 * timed out — or it dropped and could not come back: LiveKit rides out a short
 * drop by itself and reports Disconnected only once it has given up. The call
 * cannot go on, so it ends as the moderator's end ends it, back in the team's
 * room, and the player is told why in their language. A room left on purpose
 * reports nothing; the provider stops listening to it first.
 */
export function handlePrivateRoomDisconnected(): void {
  const { inPrivateChat, connectionState, moderatorName } = usePrivateVoiceChatStore.getState();
  // Over already: the room said so before, or the moderator ended the call.
  if (!inPrivateChat) return;

  endCallSaying(
    connectionState === 'connected'
      ? i18n.t('privateChat.connectionLost', { moderatorName })
      : i18n.t('privateChat.couldNotConnect', { moderatorName }),
  );
}

/**
 * Ends a call that cannot go on, the server told, and warns the player why —
 * and that they are going back to the team's room, when they are.
 */
function endCallSaying(title: string): void {
  const backToTeam = endPrivateCall({ tellServer: true });
  showWarningToast(title, backToTeam ? i18n.t('privateChat.backToTeam') : undefined);
}

/**
 * Whether the call's moderator is in its room, as its participants say
 * (voice-chat-provider.tsx), told as it changes and as the room connects. A
 * connected call whose moderator is not in it waits MODERATOR_GONE_GRACE_MS —
 * their page reloading comes back within it — and then ends as the
 * moderator's end ends it: the player back in the team's room, the server
 * told, and the player told why. Before the room connects nobody's presence
 * is known, and nothing counts.
 */
export function handleCallModeratorPresent(present: boolean): void {
  const { inPrivateChat, connectionState } = usePrivateVoiceChatStore.getState();
  if (present || !inPrivateChat || connectionState !== 'connected') {
    moderatorGoneClock.stop();
    return;
  }
  if (moderatorGoneClock.running()) return;
  moderatorGoneClock.start(MODERATOR_GONE_GRACE_MS, () => {
    const { inPrivateChat: stillInCall, moderatorName } = usePrivateVoiceChatStore.getState();
    if (stillInCall) endCallSaying(i18n.t('privateChat.moderatorLeft', { moderatorName }));
  });
}

/**
 * An error in the private room (LiveKit's onError, from
 * voice-chat-provider.tsx), for the log alone: the room's failure arrives as
 * its Disconnected (handlePrivateRoomDisconnected), and an error here can be
 * the microphone alone, which MicrophoneController reports and the call
 * outlives.
 */
export function handlePrivateRoomError(error: Error): void {
  console.error('[PrivateVoiceChat] Error in the private room:', error);
}

/**
 * The private room has connected (LiveKit's Connected, from
 * voice-chat-provider.tsx): the call is live, and only now is the player told
 * so. Accepting the invitation is not enough — the room can still fail
 * between the two.
 */
export function handlePrivateRoomConnected(): void {
  const { inPrivateChat, connectionState, moderatorName, setConnectionState } = usePrivateVoiceChatStore.getState();
  if (!inPrivateChat || connectionState === 'connected') return;

  setConnectionState('connected');
  showInfoToast(i18n.t('privateChat.connected', { moderatorName }), undefined, 3000);
}

// ==================== SERVICE METHODS ====================

/**
 * Accepts the moderator's invitation: tells the server, keeps whether the
 * player was in the team's room and their mute, for the call's end to give
 * back, and leaves the team room for the private one, unmuted. The session's
 * voice switches stay: they apply again in the team room. Nothing is said
 * yet — the private room has still to connect (handlePrivateRoomConnected).
 */
export function acceptPrivateChatInvitation(): void {
  // Once: the player's press and the invitation's own clock can both come.
  if (!usePrivateVoiceChatStore.getState().isPending) return;
  invitationClock.stop();

  void sendToMatch(OpCode.OpCodePrivateChatAccepted, { type: MessageType.MessageTypePrivateChatAccepted }, "the private chat's acceptance");

  // Out of the team room — its token is spent; the way back gets a fresh one
  // — and into the private one. What the end gives back is kept once, from
  // the team room: an invitation accepted during a call (the one it replaces
  // never ended) keeps what the first kept, since the call's unmuted
  // microphone is not the player's.
  const returnTo = suspendTeamVoiceForCall();
  usePrivateVoiceChatStore.getState().acceptPrivateChat(returnTo);
}

/**
 * Tells the server the player is out of the private call, for it to clear the
 * mark and send a fresh team token. With no connection there is nothing to
 * tell: a rejoin starts the player afresh on the server, token and all.
 */
function sendPrivateChatLeft(): void {
  void sendToMatch(OpCode.OpCodePrivateChatLeft, { type: MessageType.MessageTypePrivateChatLeft }, "the private chat's end");
}
